import type { JSX } from "react";
import {
  createFileRoute,
  useParams,
  useRouteContext,
  useRouter,
  useSearch,
} from "@tanstack/react-router";
import { graphql } from "@repo/graphql-types/gql";
import Left from "@repo/icons/default/Left";
import {
  HeaderButton,
  HeaderRoot,
  HeaderTitle,
} from "@/components/ui/layout/header/header";
import { Page } from "@/components/ui/layout/page/page";
import { useGraphQL } from "@/hooks/use-graphql";
import { AsyncDataWrapper } from "@/components/ui/async-data-wrapper";
import { CreateNewBoleto } from "@/components/payment/create-new-boleto";
import { BoletoDetails } from "@/components/payment/details/boleto-details";

const BoletoPageQuery = graphql(`
  query BoletoPageQuery($billingId: Int!) {
    cobrancasFechadas(where: { codCobranca: { eq: $billingId } }) {
      billings: nodes {
        temBoletoValido
        ...CreateNewBoletoFragment
        ...BoletoDetailsFragment
      }
    }
  }
`);

const BoletoPage = (): JSX.Element => {
  const { billingId } = useParams({
    from: "/settings/billing/details/$billingId/boleto",
  });
  const params: { from?: string } = useSearch({ strict: false });
  const router = useRouter();

  const parsedBillingId = Number(billingId);
  const { user } = useRouteContext({ from: "__root__" });

  const queryData = useGraphQL(
    BoletoPageQuery,
    {
      billingId: parsedBillingId,
    },
    {
      staleTime: Infinity,
    },
  );

  const { data } = queryData;
  const billing = data?.cobrancasFechadas?.billings?.[0];
  // biome-ignore lint/complexity/useOptionalChain: legacy code
  const hasValidBoleto = billing && billing.temBoletoValido;

  const handleGoBack = (): void => {
    if (params.from === "billing-actions") {
      router.history.go(-2);
      return;
    }

    router.history.back();
  };

  return (
    <>
      <HeaderRoot>
        <HeaderButton icon={Left} align="start" onClick={handleGoBack} />
        <HeaderTitle title="Boleto" align="center" />
      </HeaderRoot>
      <Page>
        <AsyncDataWrapper {...queryData}>
          {billing ? (
            // biome-ignore lint/complexity/noUselessFragments: legacy code
            <>
              {!hasValidBoleto && !user.pj ? (
                <CreateNewBoleto data={billing} />
              ) : (
                <BoletoDetails data={billing} />
              )}
            </>
          ) : null}
        </AsyncDataWrapper>
      </Page>
    </>
  );
};

export const Route = createFileRoute("/settings/billing/details/$billingId/boleto")({
  component: BoletoPage,
});
